import axios from 'axios';
import { html, css } from 'lit-element';
import { TmnToolBase } from '@trumedianetworks/tmn-core-components/src/tmn-tool-base';
import '@trumedianetworks/tmn-core-components/src/tmn-icon';
import '@github/clipboard-copy-element';

class GenerateTokenBtn extends TmnToolBase {
    constructor() {
        super();
        this.disabled = false;
    }

    static get properties() {
        return {
            disabled: {
                type: Boolean,
            },
            eventData: {
                type: Object,
            },
            eUserId: {
                type: String,
            },
            loginToken: {
                type: String,
            },
            copied: {
                type: Boolean,
            },
            copiedLink: {
                type: Boolean,
            },
            error: {
                type: String,
            },
        };
    }

    static get styles() {
        return [
            super.styles,
            css`
                .modal-content {
                    padding: 8px;
                    width: 265px;
                    min-height: 150px;
                }

                .confirmation-label {
                    overflow-wrap: break-word;
                    text-align: center;
                    white-space: normal;
                }
                .footer {
                    padding: 0 6px;
                }
                .error-message {
                    color: var(--app-error-color);
                    padding: 12px;
                    justify-content: center;
                }
                clipboard-copy {
                    display: flex;
                    flex-direction: row;
                    justify-content: center;
                    align-items: center;
                    --app-success-color: #00695c;
                }
                clipboard-copy > * {
                    margin: 4px;
                    margin-bottom: 16px;
                }
                input {
                    width: 200px;
                    background: var(--light-gray-100);
                    padding: 4px;
                    border: 1px solid var(--light-gray-200);
                }
            `,
        ];
    }

    updated(changedProps) {
        if (changedProps.has('eUserId')) {
            this.loginToken = '';
            this.copied = false;
            this.copiedLink = false;
            this.error = null;
        }
    }

    getToolTitle() {
        return 'Login Token';
    }

    renderDetails() {
        const tokenWithParam = `&pbtoken=${this.loginToken}`;
        return html`<div class="explainer">
                This token can be used to temporarily sign-in this user instead of a password:
            </div>
            <clipboard-copy @clipboard-copy=${this.handleCopy} value=${this.loginToken}>
                ${this.copied
                    ? html`<tmn-icon color="success" icon="done" iconvariant="outlined"></tmn-icon>`
                    : html`<tmn-icon icon="copy" iconvariant="outlined"></tmn-icon>`}
                <input class="token-input" value=${this.loginToken} />
            </clipboard-copy>

            <div class="explainer">
                You add this token to the end of a url in the following format:
            </div>
            <clipboard-copy @clipboard-copy=${this.handleCopyLink} value=${tokenWithParam}>
                ${this.copiedLink
                    ? html`<tmn-icon color="success" icon="done" iconvariant="outlined"></tmn-icon>`
                    : html`<tmn-icon icon="copy" iconvariant="outlined"></tmn-icon>`}
                <input class="token-input" value=${tokenWithParam} /> </clipboard-copy
            >${this.error ? html`<div class="error-message">Error: ${this.error}</div>` : null}`;
    }

    renderToolContent() {
        return html`
            <tmn-grid class="modal-content" container direction="row" justify="center" spacing="1">
                ${this.renderDetails()}
            </tmn-grid>
        `;
    }

    renderButton() {
        return html`<tmn-button @click=${this.handleGenerateToken} .eventData=${this.eventData}
            >Generate Login Token
        </tmn-button>`;
    }

    handleCopy(e) {
        //consider wrapping in tmn-clipboard-copy and passing through the specifics
        this.copied = true;
    }
    handleCopyLink(e) {
        //consider wrapping in tmn-clipboard-copy and passing through the specifics
        this.copiedLink = true;
    }
    renderToolFooter() {
        return html`
            <tmn-grid class="footer" container direction="row" justify="center" spacing="1">
                <tmn-grid item>
                    <tmn-button
                        class="confirm-button"
                        variant="outlined"
                        color="primary"
                        @click="${this._handleConfirm}">
                        Close
                    </tmn-button>
                </tmn-grid>
            </tmn-grid>
        `;
    }

    handleGenerateToken() {
        const { eventData, loginToken } = this;
        this._toggleDialog();
        if (!loginToken) {
            this.fetchToken(eventData);
        }
    }

    fetchToken({ eUserId, eSiteId }) {
        const params = { eUserId, eSiteId };
        const headers = {
            Accept: 'application/json',
        };

        axios
            .request({
                url: '/v2/api/user-admin/generate-login-token',
                method: 'POST',
                headers,
                withCredentials: true,
                data: params,
            })
            .then((data) => this.handleTokenResponse(data))
            .catch((err) => this.handleTokenError(err));
    }

    handleTokenResponse({ statusText, data }) {
        // todo better missing messages
        if (statusText === 'OK' && data.success) {
            this.loginToken = data.pbTempToken || 'Token Missing';
            return;
        }
        this.error = data?.message || 'Unexpected error getting token';
    }

    handleTokenError(err) {
        const { response } = err;
        const { data, status, statusText } = response || {};

        if (data?.message) {
            this.error = data.message;
        } else if (status) {
            this.error = statusText
                ? `${status} ${statusText}`
                : `Request failed with status ${status}`;
        } else if (err.isAxiosError) {
            this.error = err.message || 'Unexpected error getting token';
        } else {
            this.error = 'Unexpected error getting token';
        }

        console.error(err);
    }

    _handleConfirm(e) {
        this._toggleDialog();
    }
}

window.customElements.define('generate-token-btn', GenerateTokenBtn);
